import { jwtDecode } from 'jwt-decode'
import { setupLayouts } from 'virtual:meta-layouts'
import type { App } from 'vue'

import type { RouteRecordRaw } from 'vue-router/auto'

import { createRouter, createWebHistory } from 'vue-router/auto'
import { useUserStore } from '@/stores/useUserStore'

function recursiveLayouts(route: RouteRecordRaw): RouteRecordRaw {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++)
      route.children[i] = recursiveLayouts(route.children[i])

    return route
  }

  return setupLayouts([route])[0]
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to) {
    if (to.hash)
      return { el: to.hash, behavior: 'smooth', top: 60 }

    return { top: 0 }
  },
  extendRoutes: pages => [
    ...[...pages].map(route => recursiveLayouts(route)),
  ],
})

router.beforeEach(async (to, _, next) => {
  let loginPage
  loginPage = `https://accounts.arch.software/login?applicationId=e02175cb-1896-42b1-9cdd-0835426b262b&redirect=${window.location.origin}${to.fullPath}`

  if (to.query.token) {
    const token = to.query.token as string
    const decoded = jwtDecode<{ exp?: number }>(token)
    const maxAge = decoded.exp
      ? decoded.exp - Math.floor(Date.now() / 1000)
      : 60 * 60 * 24

    const accessToken = useCookie('membershipToken', {
      maxAge,
    })

    accessToken.value = token

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/v1/cms/hotel-chains/header`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      if (response.ok) {
        const result = await response.json()
        const userLevel = result.data?.userLevel
        if (userLevel !== undefined) {
          const userStore = useUserStore()
          userStore.setUserLevel(userLevel)

          if (userLevel === 1) {
            return next({ path: '/' })
          } else if (userLevel === 2) {
            return next({ path: '/chain-configuration' })
          } else if (userLevel === 3) {
            return next({ path: '/butler-chat' })
          }
        }
      }
    } catch (err) {
      console.error('Error fetching user level:', err)
    }

    return next({ path: '/' })
  }

  if (!useCookie('membershipToken').value) {
    window.location.replace(loginPage)

    return
  }

  return next()
})

export { router }

import { themeConfig } from '@themeConfig'

router.afterEach(() => {
  document.title = themeConfig.app.title
})

export default function (app: App) {
  app.use(router)
}
